import React, { FC, MouseEvent, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Button } from '@src/atoms/Button';
import { Icon, TIconName } from '@src/atoms/Icon';
import { classnames } from '@src/utils/classnames';
import { filesize } from 'filesize';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { SwipeDialog } from '@src/molecules/SwipeDialog';
import { MEDIA_UPLOADER_FILE } from '@src/atoms/MediaUploader/const';
import { useHovered } from '@src/hooks/propsHooks/useHovered';
import { Loader } from '@src/atoms/Loader';
import { IUploaderFile } from '../../../UploaderController';
import { UploaderContext } from '../../../context';

import styled from './UploaderFileListItem.scss';
import { Box } from '@src/atoms/Box';
import { ImageRenderer } from '@src/atoms/ImageRenderer';
import { Text } from '@src/atoms/Text';
import { getClasses } from '@src/utils/getClass';
import { TMediaUploaderPreviewMode } from '@src/atoms/MediaUploader/models';

interface IUploaderFileListItemProps {
  file: IUploaderFile;
  idx: number;
  onRemove: (idx: number) => void;
  onZoom: (index: number) => void;
  previewMode: TMediaUploaderPreviewMode;
  showDeleteButton: boolean;
}

export const UploaderFileListItem: FC<IUploaderFileListItemProps> = ({
  file,
  idx,
  onRemove,
  onZoom,
  previewMode,
  showDeleteButton,
}) => {
  const { isMobile } = useContext(BreakpointsContext);
  const { uploadFile, multiItemExtention: MultiItemExtention } = useContext(UploaderContext);
  const { hoveredCallbacks, isHovered } = useHovered();
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const clickTimeStamp = useRef(0);

  const handleZoom = useCallback(() => {
    !file.loading && !file.loadingError && onZoom?.(idx);
  }, [file.loading, file.loadingError, idx, onZoom]);

  /**
   * Это костыль для определения клика
   * Эксперементальным путем определено если время между onMouseDown и onMouseUp минимально похожее на обычный клик,
   * значит распознаем это как клик
   *  */
  const onClickMobileCallback = useMemo(
    () => ({
      onTouchStart: () => {
        clickTimeStamp.current = performance.now();
      },
      onTouchEnd: () => {
        if (performance.now() - clickTimeStamp.current < 400) {
          handleZoom();
        }
        clickTimeStamp.current = 0;
      },
    }),
    [handleZoom],
  );

  const handleRemove = useCallback(() => {
    onRemove(idx);
  }, [idx, onRemove]);

  const handleCloseDialog = () => setIsOpenDialog(false);
  const handleOpenDialog = () => setIsOpenDialog(true);

  const handleRetryUpload = useCallback(() => {
    uploadFile(file, true);
  }, [uploadFile, file]);

  const dialogActions: { icon: TIconName; title: string; cb: () => void }[] = useMemo(() => {
    const actionsArr: { icon: TIconName; title: string; cb: () => void }[] = [
      {
        icon: 'trash',
        title: 'Удалить',
        cb: () => {
          handleCloseDialog();
          handleRemove();
        },
      },
    ];
    if (file.loadingError)
      actionsArr.unshift({
        icon: 'retry',
        title: 'Повторить отправление',
        cb: () => {
          handleCloseDialog();
          handleRetryUpload();
        },
      });
    return actionsArr;
  }, [handleRemove, file.loadingError, handleRetryUpload]);

  const handleClasses = classnames(styled.handle, !isMobile && styled.handleIcon);
  const rootClasses = classnames(
    styled.root,
    !!MultiItemExtention && styled.borderRadius,
    isMobile && styled.rootMobile,
  );

  const imgClasses = classnames(
    styled.img,
    ...getClasses(styled, {
      previewMode,
    }),
  );
  const skeletonClasses = classnames(
    styled.skeletonBox,
    ...getClasses(styled, {
      previewMode,
    }),
  );
  const resizeClasses = classnames(
    styled.resizeIcon,
    ...getClasses(styled, {
      previewMode,
    }),
  );

  const lastDotIndex = file.originalName.lastIndexOf('.');
  const firstPartFileName = file.originalName.slice(0, lastDotIndex - 2).trim();
  const secondPartFileName = file.originalName.slice(lastDotIndex - 2, file.originalName.length).replace(' ', '');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    handleOpenDialog();
  };
  //иконки с display: none нужны для подгрузки этих иконок для возможности использования их в офлайн режиме
  return (
    <Box
      fullWidth
      data-elem={MEDIA_UPLOADER_FILE}
      alignItems={'center'}
      className={rootClasses}
      bgColor={isHovered ? 'basicGreyLight' : undefined}
      {...(isMobile ? {} : hoveredCallbacks)}
    >
      {!isMobile && (
        <Box className={handleClasses}>
          {isHovered && <Icon name={'dragAndDropHandler'} size={'md-light'} view={'basicBlack'} />}
        </Box>
      )}
      <Box column fullWidth>
        <Icon name={'circularAttention'} className={styled.displayNoneIcon} />
        <Icon name={'retry'} className={styled.displayNoneIcon} />

        <Box alignItems="center" gap={{ DESKTOP: 16, MOBILE: 8 }} className={styled.content}>
          <Box className={isMobile ? handleClasses : ''} onClick={isMobile ? handleZoom : undefined}>
            <Box
              justifyContent={'center'}
              className={styled.resizeContainer}
              {...(isMobile ? onClickMobileCallback : {})}
            >
              {isHovered && !file.loading && !file.loadingError && (
                <Box className={resizeClasses} alignItems={'center'} justifyContent={'center'} onClick={handleZoom}>
                  <Icon name={'resize'} view={'basicWhite'} size={'md'} />
                </Box>
              )}
              {file.loading && (
                <Box
                  className={skeletonClasses}
                  bgColor="basicGreySemiLight"
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Loader size={28} />
                </Box>
              )}

              {file.loadingError && (
                <Box
                  className={skeletonClasses}
                  bgColor="basicGreySemiLight"
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Icon name={'circularAttention'} view={'statusErrorLight'} size={'md'} />
                </Box>
              )}
              {!file.loading && !file.loadingError && (
                <Box className={imgClasses} bgColor="basicGreySemiLight">
                  <ImageRenderer url={file.previewUrl} className={imgClasses} uploadImmediately />
                </Box>
              )}
            </Box>
          </Box>

          <Box column alignItems="start" gap={{ DESKTOP: 4 }}>
            <Box>
              <Text
                id="originalFileName"
                noOfLines={1}
                weight={'bold'}
                size={'secondary'}
                className={styled.text}
                color={'basicBlack'}
              >
                {firstPartFileName}
              </Text>
              <Text id="originalFileName" weight={'bold'} size={'secondary'} color={'basicBlack'}>
                {secondPartFileName}
              </Text>
            </Box>

            {isMobile && file.loadingError && (
              <Box justifyContent={'center'} gap={4}>
                <Text id="uploadErrorText" color={'statusErrorText'} size={'secondary'}>
                  Не удалось загрузить файл
                </Text>
              </Box>
            )}

            {!isMobile && file.loadingError && (
              <Box justifyContent={'center'} gap={4}>
                <Text id="uploadErrorText" color={'statusErrorText'} size={'secondary'}>
                  Не удалось загрузить файл
                </Text>
                <Text id="uploadErrorText" color={'basicBlack'} size={'secondary'}>
                  -
                </Text>
                <Text
                  id="retryUploadText"
                  color={'basicBlack'}
                  size={'secondary'}
                  textDecoration="underline"
                  onClick={handleRetryUpload}
                  className={styled.textCursor}
                >
                  Повторить
                </Text>
              </Box>
            )}

            {file.fileSize && !file.loadingError && (
              <Box justifyContent={'center'}>
                <Text id="originalFileSize" color={'basicGreySemiDark'} size={'secondary'}>
                  {`${filesize(file.fileSize, { base: 2, standard: 'jedec' })}`}
                </Text>
              </Box>
            )}
          </Box>

          {isMobile ? (
            <Box alignItems="center" justifyContent="center" className={styled.deleteBtn}>
              <Button
                view={'textLight'}
                preIcon={'dotsHorizontal'}
                onClick={handleClick}
                id={'removeBtn'}
                iconSize={16}
              />
            </Box>
          ) : (
            <Box alignItems="center" justifyContent="center" className={styled.deleteBtn}>
              {(showDeleteButton || isHovered) && (
                <Button view={'textLight'} preIcon={'trash'} onClick={handleRemove} id={'removeBtn'} iconSize={20} />
              )}
            </Box>
          )}
        </Box>

        <SwipeDialog isOpen={isOpenDialog} onClose={handleCloseDialog} className={styled.swipeDialog}>
          <Box fullWidth alignItems="center" justifyContent="center" className={styled.swipeDialogTile}>
            <Text id="UploaderFileListItemDialogtitle" weight="bold">
              Действия
            </Text>
          </Box>

          <Box fullWidth column gap={8} paddingLeft={16} paddingRight={16} paddingTop={12} paddingBottom={56}>
            {dialogActions.map(({ cb, icon, title }) => {
              return (
                <Button
                  key="title"
                  size="md"
                  preIcon={icon}
                  id={`UploaderFileListItemDialogBtn_${icon}`}
                  onClick={cb}
                  view="actionItem"
                >
                  {title}
                </Button>
              );
            })}
          </Box>
        </SwipeDialog>
        {MultiItemExtention && <MultiItemExtention file={file} />}
      </Box>
    </Box>
  );
};
